import { _PdfContentStream } from '../base-stream';
import { _ContentParser } from '../content-parser';
import { PdfPrintState } from '../enumerator';
import { PdfDocument } from '../pdf-document';
import { _PdfDictionary, _PdfName, _PdfReference } from '../pdf-primitives';
import { _getNewGuidString } from '../utils';
import { PdfLayer } from './layer';
/**
 * The class provides methods and properties to handle the collection of `PdfLayer`.
 * ```typescript
 * // Load an existing PDF document
 * let document: PdfDocument = new PdfDocument(data);
 * // Access the collection of layers in the document
 * let layers: PdfLayerCollection = document.layers;
 * // Save the document
 * document.save('output.pdf');
 * // Destroy the document
 * document.destroy();
 * ```
 */
export class PdfLayerCollection {
    constructor(document, layer) {
        this._subLayer = false;
        this._isLayerContainsResource = false;
        this._layerDictionary = new Map();
        this._bdcCount = 0;
        if (!this._list) {
            this._list = [];
        }
        this._crossReference = document._crossReference;
        this._catalog = document._catalog;
        this._document = document;
        if (layer) {
            this._parent = layer;
        }
        else {
            let _layerDictionary;
            let _layerReference;
            if (this._document && this._document._catalog && this._document._catalog._catalogDictionary
                && this._document._catalog._catalogDictionary.has('OCProperties')) {
                const ocProperties = this._document._catalog._catalogDictionary.get('OCProperties');
                if (ocProperties && ocProperties.has('OCGs')) {
                    const ocGroup = ocProperties.get('OCGs');
                    if (ocGroup && Array.isArray(ocGroup)) {
                        ocGroup.forEach((item) => {
                            _layerReference = item;
                            if (_layerReference instanceof _PdfReference) {
                                _layerDictionary = this._crossReference._fetch(_layerReference);
                                const layer = new PdfLayer();
                                if (_layerDictionary) {
                                    if (_layerDictionary.has('Name')) {
                                        const layerName = _layerDictionary.get('Name');
                                        layer.name = layerName;
                                        layer._dictionary = _layerDictionary;
                                        layer._crossReference = this._document._crossReference;
                                        layer._referenceHolder = _layerReference;
                                        const layerId = _layerDictionary.get('LayerID');
                                        if (layerId) {
                                            layer._layerId = layerId.name;
                                        }
                                        const _print = _layerDictionary.getRaw('Usage');
                                        if (_print && _print instanceof _PdfDictionary) {
                                            const printOption = _print.get('Print');
                                            if (printOption && printOption instanceof _PdfDictionary) {
                                                layer._printOption = printOption;
                                                if (printOption.has('PrintState')) {
                                                    this._setPrintState(printOption, layer);
                                                }
                                            }
                                            const viewState = _print.get('View');
                                            if (viewState && viewState instanceof _PdfDictionary && viewState.has('ViewState')) {
                                                const view = viewState.get('ViewState');
                                                if (view.name === 'OFF') {
                                                    layer.visible = false;
                                                }
                                            }
                                        }
                                        if (_print && _print instanceof _PdfReference) {
                                            const printRef = this._crossReference._fetch(_print);
                                            if (printRef && printRef instanceof _PdfDictionary) {
                                                const _printOptionReference = printRef.getRaw('Print');
                                                if (_printOptionReference && _printOptionReference instanceof _PdfReference) {
                                                    const subtytpe = this._crossReference._fetch(_printOptionReference);
                                                    if (subtytpe && subtytpe instanceof _PdfDictionary) {
                                                        layer._printOption = subtytpe;
                                                        if (subtytpe.has('PrintState')) {
                                                            this._setPrintState(subtytpe, layer);
                                                        }
                                                    }
                                                }
                                                const viewStateReference = printRef.getRaw('View');
                                                if (viewStateReference && viewStateReference instanceof _PdfReference) {
                                                    const viewState = this._crossReference._fetch(viewStateReference);
                                                    if (viewState && viewState instanceof _PdfDictionary && viewState.has('ViewState')) {
                                                        const view = viewState.get('ViewState');
                                                        if (view.name === 'OFF') {
                                                            layer.visible = false;
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    layer._document = document;
                                    layer._layer = layer;
                                    this._layerDictionary.set(_layerReference, layer);
                                    this._list.push(layer);
                                }
                            }
                        });
                    }
                    this._checkLayerLock(ocProperties);
                    this._checkLayerVisible(ocProperties);
                    this._checkParentLayer(ocProperties);
                    this._createLayerHierarchical(ocProperties);
                }
            }
        }
    }
    get _isSkip() {
        return this._bdcCount > 0;
    }
    /**
     * Gets the layer count.
     *
     * @returns {number} Number of layers.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data);
     * // Access the collection of layers in the document
     * let layers: PdfLayerCollection = document.layers;
     * // Retrieve layer counts from the layers collection
     * let count: number = layers.count;
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    get count() {
        return this._list.length;
    }
    /**
     * Gets the `PdfLayer` at the specified index.
     *
     * @param {number} index Layer index.
     * @returns {PdfLayer} Layer at the specified index.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data);
     * // Access the collection of layers in the document
     * let layers: PdfLayerCollection = document.layers;
     * // Retrieve the first layer from the layers collection
     * let layer: PdfLayer = layers.at(0);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    at(index) {
        return this._list[index];
    }
    add(name, visible) {
        const newLayer = new PdfLayer();
        newLayer._document = this._document;
        newLayer._crossReference = this._document._crossReference;
        newLayer.name = name;
        if (visible !== null && typeof visible !== 'undefined') {
            newLayer.visible = visible;
        }
        newLayer._layerId = 'OCG_' + _getNewGuidString();
        newLayer._subLayerPosition = 0;
        newLayer._layer = newLayer;
        this._addLayer(newLayer);
        return newLayer;
    }
    contains(arg) {
        if (!arg) {
            throw new Error('Layer cannot be null or undefined');
        }
        if (typeof arg === 'string') {
            return this._list.some((layer) => layer.name === arg);
        }
        else if (arg instanceof PdfLayer) {
            if (this._list.indexOf(arg) !== -1) {
                return true;
            }
        }
        return false;
    }
    /**
     * Remove all the layers.
     *
     * @returns {void} Returns nothing.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data);
     * // Access the collection of layers in the document
     * let layers: PdfLayerCollection = document.layers;
     * // Removes all layers from the collection
     * layers.clear();
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    clear() {
        for (let i = this._list.length - 1; i > -1; i--) {
            const layer = this._list[i];
            this._removeLayer(layer, true);
        }
        this._list.length = 0;
    }
    /**
     * Index of the specified layer.
     *
     * @param {PdfLayer} layer The layer to be checked.
     * @returns {number} Index of the layer.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data);
     * // Access the collection of layers in the document
     * let layers: PdfLayerCollection = document.layers;
     * // Add a new layer to the document with the name 'Layer1'
     * let layer: PdfLayer = layers.add('Layer1');
     * // Find the index of the layer in the layers collection
     * let index: number = layers.indexOf(layer);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    indexOf(layer) {
        if (!layer) {
            throw new Error('Layer cannot be null or undefined');
        }
        return this._list.indexOf(layer);
    }
    /**
     * Move the `PdfLayer` into the collection at specified index.
     *
     * @param {number} index Index of the layer.
     * @param {PdfLayer} layer Layer to move.
     * @returns {void} Returns nothing.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data);
     * // Access the collection of layers in the document
     * let layers: PdfLayerCollection = document.layers;
     * // Add a new layer to the document with the name 'Layer1'
     * let layer: PdfLayer = layers.add('Layer1');
     * // Add a new layer to the document with the name 'Layer2'
     * let layer1: PdfLayer = layers.add('Layer2');
     * // Move 'layer2' to the first position (index 0)
     * layers.move(0, layer2);
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    move(index, layer) {
        if (index < 0 || index >= this._list.length) {
            throw new Error('Index cannot be less than 0 or greater than array length');
        }
        if (!layer) {
            throw new Error('Layer cannot be null or undefined');
        }
        let position;
        for (let i = 0; i < this._list.length; i++) {
            if (this._list[i] === layer) {
                position = this.indexOf(this._list[i]);
                break;
            }
        }
        if (position !== null && typeof position !== 'undefined' && position !== index) {
            this._list.splice(position, 1);
            this._list.splice(index, 0, layer);
            this._insertLayer(index, layer);
        }
    }
    removeAt(arg1, arg2) {
        if (arg1 < 0 || arg1 >= this._list.length) {
            throw new Error('Index cannot be less than 0 or greater than array length');
        }
        const layer = this._list[arg1];
        this._list.splice(arg1, 1);
        if (layer) {
            this._removeLayer(layer, arg2 || false);
            if (layer._child.length > 0) {
                for (const child of layer._child) {
                    this._removeLayer(child, false);
                    const index = this._list.indexOf(child);
                    if (index !== -1) {
                        this._list.splice(index, 1);
                    }
                }
            }
        }
    }
    remove(arg1, arg2) {
        if (arg1 instanceof PdfLayer) {
            const layer = arg1;
            const index = this._list.indexOf(layer);
            if (index !== -1) {
                this.removeAt(index, arg2 || false);
            }
        }
        else if (typeof arg1 === 'string') {
            for (let i = 0; i < this._list.length; i++) {
                const layer = this._list[i];
                const index = this._list.indexOf(layer);
                if (layer.name === arg1 && index !== -1) {
                    this.removeAt(index, arg2 || false);
                    i = i - 1;
                }
            }
        }
    }
    _setPrintState(printOption, layer) {
        const printState = printOption.get('PrintState');
        if (printState && printState instanceof _PdfName) {
            if (printState.name === 'ON') {
                layer.printState = PdfPrintState.alwaysPrint;
            }
            else {
                layer.printState = PdfPrintState.neverPrint;
            }
        }
    }
    _addLayer(layer) {
        this._list.push(layer);
        const index = this._list.length - 1;
        if (this._document instanceof PdfDocument) {
            this._createLayer(layer);
        }
        layer._layer = layer;
        return index;
    }
    _createLayer(layer) {
        const ocProperties = new _PdfDictionary(this._crossReference);
        const ocGroups = this._createOptionalContentDictionary(layer);
        let isPresent = false;
        if (this._document && this._document._catalog && this._document._catalog._catalogDictionary.has('OCProperties') && this._isLayerContainsResource) {
            const _ocDictionary = this._document._catalog._catalogDictionary.get('OCProperties');
            if (_ocDictionary && _ocDictionary.has('OCGs')) {
                const ocgsList = _ocDictionary.get('OCGs');
                if (ocgsList && ocGroups) {
                    isPresent = true;
                    for (const obj of ocGroups) {
                        if (ocgsList.indexOf(obj) === -1) {
                            ocgsList.push(obj);
                        }
                    }
                }
                if (_ocDictionary.has('D')) {
                    const defaultView = _ocDictionary.get('D');
                    if (defaultView) {
                        let on;
                        let off;
                        let _usage;
                        if (!defaultView.has('Order')) {
                            defaultView.update('Order', this._document._order);
                        }
                        if (defaultView.has('OFF')) {
                            off = defaultView.get('OFF');
                        }
                        if (defaultView.has('ON')) {
                            on = defaultView.get('ON');
                        }
                        if (defaultView.has('AS')) {
                            _usage = defaultView.get('AS');
                        }
                        if (_usage) {
                            _usage.forEach((item) => {
                                let usageDictionary;
                                const value = item;
                                if (value instanceof _PdfReference) {
                                    usageDictionary = this._crossReference._fetch(value);
                                    if (usageDictionary && usageDictionary instanceof _PdfDictionary) {
                                        const usageOcGroup = usageDictionary.get('OCGs');
                                        if (usageOcGroup && ocGroups && usageOcGroup.indexOf(layer._referenceHolder) === -1) {
                                            usageOcGroup.push(layer._referenceHolder);
                                        }
                                    }
                                }
                            });
                        }
                        if (layer.visible) {
                            if (on && ocGroups && on.indexOf(layer._referenceHolder) === -1) {
                                on.push(layer._referenceHolder);
                            }
                        }
                        else if (off && ocGroups && off.indexOf(layer._referenceHolder) === -1) {
                            off.push(layer._referenceHolder);
                        }
                    }
                }
                this._document._catalog._catalogDictionary._updated = true;
            }
        }
        if (!isPresent) {
            ocProperties.update('OCGs', ocGroups);
            ocProperties.update('D', this._createOptionalContentViews());
            this._catalog._catalogDictionary.update('OCProperties', ocProperties);
        }
        this._crossReference._allowCatalog = true;
    }
    _createOptionalContentDictionary(layer) {
        const _dictionary = new _PdfDictionary(this._crossReference);
        const reference = this._crossReference._getNextReference();
        this._crossReference._cacheMap.set(reference, _dictionary);
        _dictionary.update('Name', layer.name);
        _dictionary.update('Type', new _PdfName('OCG'));
        _dictionary.update('LayerID', new _PdfName(layer._layerId));
        _dictionary.update('Visible', layer.visible);
        if (layer.printState === PdfPrintState.alwaysPrint ||
            layer.printState === PdfPrintState.neverPrint ||
            layer.printState === PdfPrintState.printWhenVisible) {
            const UsageReference = this._setPrintOption(layer);
            _dictionary.update('Usage', UsageReference);
            this._document._printLayer.push(reference);
        }
        this._document._optionalContentDictionaries.push(reference);
        layer._dictionary = _dictionary;
        layer._referenceHolder = reference;
        const ocProperties = this._document._catalog._catalogDictionary.get('OCProperties');
        this._createSublayer(ocProperties, reference, layer);
        if (layer.visible) {
            this._document._on.push(reference);
        }
        else {
            this._document._off.push(reference);
        }
        this._isLayerContainsResource = true;
        return this._document._optionalContentDictionaries;
    }
    _createOptionalContentViews() {
        const _optionalContent = new _PdfDictionary();
        _optionalContent.update('Name', 'Layers');
        _optionalContent.update('Order', this._document._order);
        _optionalContent.update('ON', this._document._on);
        _optionalContent.update('OFF', this._document._off);
        const category = [];
        category.push(new _PdfName('Print'));
        const _usageApplication = new _PdfDictionary();
        _usageApplication.update('Category', category);
        _usageApplication.update('OCGs', this._document._printLayer);
        _usageApplication.update('Event', new _PdfName('Print'));
        const reference = this._crossReference._getNextReference();
        this._crossReference._cacheMap.set(reference, _usageApplication);
        this._document._as.push(reference);
        _optionalContent.update('AS', this._document._as);
        return _optionalContent;
    }
    _setPrintOption(layer) {
        const _usage = new _PdfDictionary();
        const _print = new _PdfDictionary();
        const usageReference = this._crossReference._getNextReference();
        const printReference = this._crossReference._getNextReference();
        this._crossReference._cacheMap.set(usageReference, _usage);
        this._crossReference._cacheMap.set(printReference, _print);
        _print.update('Subtype', new _PdfName('Print'));
        if (layer.printState === PdfPrintState.neverPrint) {
            _print.update('PrintState', new _PdfName('OFF'));
        }
        else if (layer.printState === PdfPrintState.alwaysPrint) {
            _print.update('PrintState', new _PdfName('ON'));
        }
        layer._usage = _usage;
        layer._printOption = _print;
        _usage.update('Print', printReference);
        return usageReference;
    }
    _createSublayer(ocProperties, reference, layer) {
        if (!this._subLayer) {
            if (ocProperties) {
                let order;
                const defaultview = ocProperties.get('D');
                if (defaultview) {
                    order = defaultview.get('Order');
                }
                if (order) {
                    this._document._order = order;
                }
                this._document._order.push(reference);
            }
            else {
                this._document._order.push(reference);
            }
        }
        else {
            layer._parent = this._parent;
            if (ocProperties) {
                let order;
                const defaultview = ocProperties.get('D');
                if (defaultview) {
                    order = defaultview.get('Order');
                }
                if (this._document._order && order) {
                    this._document._order = order;
                }
            }
            if (this._parent._child.length === 0) {
                this._parent._subLayer.push(reference);
            }
            else if (this._document._order.indexOf(this._parent._referenceHolder) !== -1) {
                const position = this._document._order.indexOf(this._parent._referenceHolder);
                this._document._order.splice(position + 1, 1);
                this._parent._subLayer.push(reference);
            }
            else {
                this._parent._subLayer.push(reference);
            }
            if (this._document._order.indexOf(this._parent._referenceHolder) !== -1) {
                const position = this._document._order.indexOf(this._parent._referenceHolder);
                this._document._order.splice(position + 1, 0, this._parent._subLayer);
            }
            else {
                if (this._parent._parent) {
                    if (this._parent._parent._subLayer.indexOf(this._parent._referenceHolder) !== -1) {
                        const position = this._parent._parent._subLayer.indexOf(this._parent._referenceHolder);
                        if (this._parent._subLayer.length === 1) {
                            this._parent._parent._subLayer.splice(position + 1, 0, this._parent._subLayer);
                        }
                        if (this._document._order.indexOf(this._parent._parent._referenceHolder) !== -1) {
                            const position = this._document._order.indexOf(this._parent._parent._referenceHolder);
                            this._document._order.splice(position + 1, 1);
                            this._document._order.splice(position + 1, 0, this._parent._parent._subLayer);
                        }
                    }
                }
                else {
                    if (document instanceof PdfDocument) {
                        for (let i = 0; i < document._order.length; i++) {
                            if (Array.isArray(document._order[i])) {
                                const value = document._order[i]; // eslint-disable-line
                                const orderArray = value;
                                if (orderArray.indexOf(this._parent._referenceHolder) !== -1) {
                                    const position = orderArray.indexOf(this._parent._referenceHolder);
                                    if (this._parent._subLayer.length === 1) {
                                        orderArray.splice(position + 1, 0, this._parent._subLayer);
                                        break;
                                    }
                                }
                            }
                        }
                    }
                }
            }
            if (this._parent._child.indexOf(layer) === -1) {
                this._parent._child.push(layer);
            }
            if (this._parent._parentLayer.length === 0) {
                layer._parentLayer.push(this._parent);
            }
            else {
                this._parent._parentLayer.forEach((element) => {
                    if (layer._parentLayer.indexOf(element) === -1) {
                        layer._parentLayer.push(element);
                    }
                });
                if (layer._parentLayer.indexOf(this._parent) === -1) {
                    layer._parentLayer.push(this._parent);
                }
            }
        }
    }
    _checkLayerLock(ocProperties) {
        let locked;
        const defaultView = ocProperties.get('D');
        if (defaultView && defaultView.has('Locked')) {
            locked = defaultView.get('Locked');
        }
        if (locked) {
            locked.forEach((referenceHolder) => {
                if (referenceHolder && referenceHolder instanceof _PdfReference) {
                    const pdfLayer = this._layerDictionary.get(referenceHolder);
                    if (pdfLayer) {
                        pdfLayer.locked = true;
                    }
                }
            });
        }
    }
    _checkLayerVisible(ocProperties) {
        const _document = this._document;
        let visible;
        if (_document._catalog && _document._catalog._catalogDictionary.has('OCProperties')) {
            const defaultView = ocProperties.get('D');
            if (defaultView && defaultView.has('OFF')) {
                visible = defaultView.get('OFF');
            }
            if (visible) {
                visible.forEach((visibleReference) => {
                    if (visibleReference instanceof _PdfReference) {
                        const layerDictionary = this._layerDictionary;
                        if (layerDictionary && layerDictionary.size > 0 && visibleReference && layerDictionary.has(visibleReference)) {
                            const pdfLayer = layerDictionary.get(visibleReference);
                            if (pdfLayer) {
                                pdfLayer.visible = false;
                                if (pdfLayer._dictionary && pdfLayer._dictionary.has('Visible')) {
                                    pdfLayer._dictionary.set('Visible', false);
                                }
                            }
                        }
                    }
                });
            }
        }
    }
    _checkParentLayer(ocProperties) {
        const defaultView = ocProperties.get('D');
        if (defaultView) {
            const array = defaultView.get('Order');
            if (array) {
                this._parsingLayerOrder(null, array, this._layerDictionary);
            }
        }
    }
    _parsingLayerOrder(parent, array, layerDictionary) {
        let reference;
        let layer;
        for (let i = 0; i < array.length; i++) {
            reference = array[i];
            if (reference instanceof _PdfReference) {
                if (layerDictionary.has(reference)) {
                    layer = layerDictionary.get(reference);
                }
                if (layer) {
                    if (parent) {
                        if (parent._child.indexOf(layer) === -1) {
                            parent._child.push(layer);
                        }
                        if (parent._parentLayer.length === 0) {
                            layer._parentLayer.push(parent);
                            layer._parent = parent;
                        }
                        else {
                            parent._parentLayer.forEach((parentLayerElement) => {
                                if (layer._parentLayer.indexOf(parentLayerElement) === -1) {
                                    if (!(parentLayerElement instanceof PdfLayer) && parentLayerElement) {
                                        layer._parentLayer.push(parentLayerElement);
                                    }
                                }
                            });
                            layer._parentLayer.push(parent);
                            layer._parent = parent;
                        }
                    }
                    if (array.length > i + 1 && (Array.isArray(array[i + 1]))) {
                        i++;
                        const pdfArray = array[i]; // eslint-disable-line
                        layer._subLayer = pdfArray;
                        this._parsingLayerOrder(layer, pdfArray, layerDictionary);
                    }
                }
            }
            else if (Array.isArray(array[i])) {
                const value = array[i]; // eslint-disable-line
                const subArray = value;
                if (!subArray || subArray.length === 0) {
                    return;
                }
                if (typeof subArray[0] === 'string') {
                    parent = null;
                    this._parsingLayerOrder(parent, subArray, layerDictionary);
                }
                else {
                    parent = null;
                    this._parsingLayerOrder(parent, array[i], layerDictionary); // eslint-disable-line
                }
            }
        }
    }
    _createLayerHierarchical(ocProperties) {
        const defaultView = ocProperties.get('D');
        if (defaultView && defaultView.has('Order')) {
            if (this._layerDictionary && this._layerDictionary.size > 0) {
                this._list.splice(0, this._list.length);
                this._layerDictionary.forEach((_layer, _pdfReference) => {
                    if (_layer instanceof PdfLayer && _pdfReference instanceof _PdfReference) {
                        if (!_layer._parent && this._list.indexOf(_layer) === -1) {
                            this._list.push(_layer);
                        }
                        else if (_layer._child.length > 0) {
                            this._addChildLayer(_layer._parent);
                        }
                        else if (_layer._parent && _layer._child.length === 0 && !_layer._parent.layers.contains(_layer)) {
                            _layer._parent.layers._addNestedLayer(_layer);
                        }
                    }
                });
            }
        }
    }
    _addChildLayer(layer) {
        layer._child.forEach((child) => {
            if (layer.layers.indexOf(child) === -1) {
                layer.layers._addNestedLayer(child);
            }
        });
    }
    _addNestedLayer(layer) {
        this._list.push(layer);
        const index = this._list.length - 1;
        layer._layer = layer;
        return index;
    }
    _removeLayer(layer, removeGraphicalContent) {
        let _dictionary;
        if (layer && this._document) {
            _dictionary = this._document._catalog._catalogDictionary;
            if (_dictionary && _dictionary.has('OCProperties')) {
                const ocProperties = _dictionary.get('OCProperties');
                if (ocProperties) {
                    const ocGroup = ocProperties.get('OCGs');
                    if (ocGroup) {
                        this._removeOCG(layer, ocGroup);
                    }
                    if (ocProperties.has('D')) {
                        const defaultView = ocProperties.get('D');
                        if (defaultView) {
                            let on;
                            let off;
                            if (defaultView.has('Order')) {
                                const order = defaultView.get('Order');
                                if (order) {
                                    const arrayList = [];
                                    this._removeOrder(layer, order, arrayList);
                                }
                            }
                            if (defaultView.has('Locked')) {
                                const locked = defaultView.get('Locked');
                                if (locked) {
                                    this._removeLocked(layer, locked);
                                }
                            }
                            if (defaultView.has('OFF')) {
                                off = defaultView.get('OFF');
                            }
                            if (defaultView.has('ON')) {
                                on = defaultView.get('ON');
                            }
                            if (defaultView.has('AS')) {
                                const _usage = defaultView.get('AS');
                                if (_usage) {
                                    this._removeUsage(layer, _usage);
                                }
                            }
                            this._removeVisible(layer, on, off);
                        }
                        defaultView._updated = true;
                    }
                    const page = layer._layerPage;
                    if (page) {
                        const resource = layer._layerPage._pageDictionary.get('Resources');
                        if (resource.has('Properties')) {
                            const properties = resource.get('Properties');
                            if (properties && properties.has(layer._layerId)) {
                                delete properties._map[layer._layerId];
                            }
                        }
                    }
                    const chacheMap = this._crossReference._cacheMap; // eslint-disable-line
                    if (chacheMap.has(layer._referenceHolder)) {
                        let dictionary = this._crossReference._cacheMap.get(layer._referenceHolder);
                        if (dictionary) {
                            const usage = dictionary.getRaw('Usage');
                            if (usage instanceof _PdfReference) {
                                dictionary = this._crossReference._cacheMap.get(usage);
                                if (dictionary) {
                                    const printReference = dictionary.getRaw('Print');
                                    chacheMap.delete(layer._referenceHolder);
                                    chacheMap.delete(usage);
                                    chacheMap.delete(printReference);
                                }
                            }
                        }
                    }
                    ocProperties._updated = true;
                    _dictionary._updated = true;
                }
                this._crossReference._allowCatalog = true;
            }
            if (removeGraphicalContent) {
                this._removeLayerContent(layer);
            }
        }
    }
    _removeOCG(layer, ocGroup) {
        if (ocGroup && ocGroup.indexOf(layer._referenceHolder) !== -1) {
            ocGroup.splice(ocGroup.indexOf(layer._referenceHolder), 1);
        }
    }
    _removeUsage(layer, _usage) {
        if (_usage) {
            let isRemoved = false;
            for (let i = 0; i < _usage.length; i++) {
                const usage = _usage[i];
                if (usage) {
                    let usageDictionary;
                    if (usage instanceof _PdfReference) {
                        usageDictionary = this._crossReference._fetch(_usage[i]);
                    }
                    if (usage instanceof _PdfDictionary) {
                        usageDictionary = usage;
                    }
                    if (usageDictionary) {
                        const usageOcGroup = usageDictionary.get('OCGs');
                        if (usageOcGroup) {
                            if (usageOcGroup.indexOf(layer._referenceHolder) !== -1) {
                                usageOcGroup.splice(usageOcGroup.indexOf(layer._referenceHolder), 1);
                                isRemoved = true;
                            }
                            if (isRemoved) {
                                break;
                            }
                        }
                    }
                }
            }
        }
    }
    _removeOrder(layer, order, arrayList) {
        let isRemoveOrder = false;
        if (order) {
            for (let i = 0; i < order.length; i++) {
                const entry = order[i];
                if (entry && entry instanceof _PdfReference && entry === layer._referenceHolder) {
                    if (i !== order.length - 1) {
                        if (Array.isArray(order[i + 1])) {
                            order.splice(i, 2);
                            isRemoveOrder = true;
                            break;
                        }
                        else {
                            order.splice(i, 1);
                            isRemoveOrder = true;
                            break;
                        }
                    }
                    else {
                        order.splice(i, 1);
                        isRemoveOrder = true;
                        break;
                    }
                }
                else if (Array.isArray(entry)) {
                    arrayList.push(order[i]);
                }
            }
        }
        if (!isRemoveOrder && arrayList) {
            for (let i = 0; i < arrayList.length; i++) {
                order = arrayList[i];
                arrayList.splice(i, 1);
                i -= 1;
                this._removeOrder(layer, order, arrayList);
            }
        }
    }
    _removeVisible(layer, on, off) {
        if (layer.visible) {
            if (on && on.indexOf(layer._referenceHolder) !== -1) {
                const index = on.indexOf(layer._referenceHolder);
                if (index > -1) {
                    on.splice(index, 1);
                }
            }
        }
        else {
            if (off && off.indexOf(layer._referenceHolder) !== -1) {
                const index = off.indexOf(layer._referenceHolder);
                if (index > -1) {
                    off.splice(index, 1);
                }
            }
        }
    }
    _removeLocked(layer, locked) {
        if (locked && locked.indexOf(layer._referenceHolder) !== -1) {
            locked.splice(locked.indexOf(layer._referenceHolder), 1);
        }
    }
    _removeLayerContent(layer) {
        let isSkip = false;
        let _properties;
        let _xObject;
        if (layer._layerPage) {
            for (let i = 0; i < layer._pages.length; i++) {
                const _resource = layer._pages[i]._pageDictionary.get('Resources');
                if (_resource) {
                    _properties = _resource.get('Properties');
                    _xObject = _resource.get('XObject');
                    if (_properties && layer._layerId.trim().length > 0 && _properties.has(layer._layerId)) {
                        delete _properties._map[layer._layerId];
                    }
                    if (_xObject && layer._xObject.length > 0) {
                        const map = _xObject._map; // eslint-disable-line
                        for (const key of map) {
                            if (layer._xObject.indexOf(map[String(key)]) !== -1) {
                                delete _xObject._map[String(key)];
                                break;
                            }
                        }
                        if (layer._xObject.indexOf(layer._layerId) !== -1) {
                            layer._xObject.splice(layer._xObject.indexOf(layer._layerId), 1);
                        }
                    }
                }
                const content = layer._pages[i]._pageDictionary.getArray('Contents'); // eslint-disable-line
                for (let m = 0; m < content.length; m++) {
                    const data = new _PdfContentStream([]);
                    const stream = content[m];
                    const objID = stream.dictionary.objId;
                    const bytes = stream.getBytes();
                    let parser;
                    if (stream instanceof _PdfContentStream) {
                        parser = new _ContentParser(stream._bytes);
                    }
                    else {
                        parser = new _ContentParser(bytes);
                    }
                    const result = parser._readContent();
                    for (let j = 0; j < result.length; j++) {
                        const entry = result[j];
                        const _operator = entry._operator;
                        if (_operator === 'BMC' || _operator === 'EMC' || _operator === 'BDC') {
                            const operands = entry._operands;
                            this._processBeginMarkContent(layer, _operator, operands, data, objID);
                            isSkip = true;
                        }
                        if (_operator === 'Do' && layer._xObject.indexOf(entry._operands[0]) !== -1) {
                            isSkip = true;
                        }
                        if (_operator === 'q' || _operator === 'Q' ||
                            _operator === 'w' || _operator === 'J' || _operator === 'j' ||
                            _operator === 'M' || _operator === 'd' || _operator === 'ri' ||
                            _operator === 'i' || _operator === 'gs' || _operator === 'g' ||
                            _operator === 'cm' || _operator === 'G' || _operator === 'rg' ||
                            _operator === 'RG' || _operator === 'k' || _operator === 'K' ||
                            _operator === 'cs' || _operator === 'CS' || _operator === 'scn' ||
                            _operator === 'SCN' || _operator === 'sc' || _operator === 'SC') {
                            if (!isSkip) {
                                this._streamWrite(entry._operands, _operator, false, data);
                            }
                            isSkip = false;
                        }
                        else {
                            if (!isSkip) {
                                this._streamWrite(entry._operands, _operator, true, data);
                            }
                            isSkip = false;
                        }
                    }
                    if (data.length > 0 && !objID) {
                        const _pages = layer._pages[i];
                        const _reference = _pages._contents[m];
                        const contentStream = this._crossReference._fetch(_reference);
                        contentStream._bytes.length = 0;
                        contentStream.write(data.getString());
                    }
                }
                layer._pages[i]._pageDictionary._updated = true;
            }
        }
    }
    _processBeginMarkContent(parser, operator, operands, data, id) {
        if (operator === 'BDC') {
            let operand;
            if (operands.length > 1 && operands[0].substring(1) === 'OC') {
                operand = operands[1].substring(1);
            }
            if (this._bdcCount > 0) {
                this._bdcCount++;
                return;
            }
            if (operand && operand === parser._layerId) {
                this._bdcCount++;
                const refArray = parser._pages[0]._pageDictionary.getRaw('Contents');
                if (id) {
                    const strParts = id.split(' ');
                    const index = refArray.indexOf(_PdfReference.get(Number(strParts[0]), Number(strParts[1])));
                    parser._pages[0]._pageDictionary.getRaw('Contents').splice(index, 1);
                }
            }
        }
        this._streamWrite(operands, operator, true, data);
        if ('EMC' === operator && this._bdcCount > 0) {
            this._bdcCount--;
        }
    }
    _streamWrite(operands, operator, skip, data) {
        let pdfString;
        if (skip && this._isSkip) {
            return;
        }
        if (operands) {
            for (const operand of operands) {
                pdfString = operand;
                data.write(pdfString);
                data.write(' ');
            }
        }
        pdfString = operator;
        data.write(pdfString);
        data.write('\r\n');
    }
    _insertLayer(index, layer) {
        const reference = layer._referenceHolder;
        if (this._document) {
            const catalog = this._document._catalog._catalogDictionary;
            if (catalog.has('OCProperties')) {
                const ocDictionary = catalog.get('OCProperties');
                if (ocDictionary) {
                    const ocGroups = ocDictionary.get('OCGs');
                    if (ocDictionary.has('D')) {
                        const defaultView = ocDictionary.get('D');
                        if (defaultView) {
                            const order = defaultView.get('Order');
                            if (order && ocGroups && order.indexOf(reference) !== -1 && index < order.length) {
                                if (order[index] instanceof _PdfReference) {
                                    if (index + 1 < order.length && index + 2 < order.length) {
                                        const first = index + 1;
                                        const second = index + 2;
                                        if (order[first] instanceof _PdfReference
                                            && order[second] instanceof _PdfReference) {
                                            const position = order.indexOf(reference);
                                            order.splice(position, 1);
                                            order.splice(index, 0, reference);
                                            if (ocGroups.indexOf(reference) !== -1) {
                                                const position = ocGroups.indexOf(reference);
                                                ocGroups.splice(position, 1);
                                                ocGroups.splice(index, 0, reference);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
